'use client';

import { c as _c } from "react/compiler-runtime";
import './sac-card.scss';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { SkeletonImage } from '@sola/ark-core/atoms/skeleton-image/skeleton-image.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { ChildImageVariantEnum, getChildImageUrl } from '@sola/ark-core/utils/child-image.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import loader from '@sola/shared-utils/ark-skippy/loader.ts';
import { Image } from '../../atoms/image/image.tsx';
import { useSacImageAltText } from '../../utils/alt-text.ts';
export const SacCard: React.FC<ChildAvailabilityItem> = t0 => {
  const $ = _c(41);
  const {
    name: t1,
    imageUrl,
    birthDate,
    icp: t2,
    minDaysWaiting,
    age,
    globalId,
    sourceCode,
    gender,
    isSpecialNeeds,
    isOrphan,
    isInHivAffectedArea
  } = t0;
  const {
    preferred: preferredName
  } = t1;
  const {
    country
  } = t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["children", "geo"];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation(t3);
  let t4;
  if ($[1] !== globalId) {
    t4 = () => {
      sessionStorage.setItem("sacSelectedChild", globalId);
    };
    $[1] = globalId;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const handleClick = t4;
  let t5;
  if ($[3] !== imageUrl) {
    t5 = getChildImageUrl(imageUrl, ChildImageVariantEnum.Full, 500);
    $[3] = imageUrl;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const childImage = t5;
  const titleAndAlt = useSacImageAltText(gender, country, undefined, minDaysWaiting, preferredName, isOrphan, isSpecialNeeds, isInHivAffectedArea);
  const t6 = `/sponsor-a-child/child-bio?gid=${globalId}&source=${sourceCode}`;
  let t7;
  if ($[5] !== minDaysWaiting || $[6] !== t) {
    t7 = t("children:child.waiting", {
      numDays: minDaysWaiting
    });
    $[5] = minDaysWaiting;
    $[6] = t;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t7) {
    t8 = <div className="SacCard__badge absolute m-comfy-xs"><Badge color="brand" label={t7} /></div>;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== childImage || $[11] !== titleAndAlt) {
    t9 = childImage ? {
      url: childImage,
      alt: titleAndAlt
    } : undefined;
    $[10] = childImage;
    $[11] = titleAndAlt;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t9) {
    t10 = <div className="SacCard__child-image-container"><Image image={t9} loader={loader} sizeIntent="third" className="SacCard__child-image" rounded="lg" hasPlaceholder={true} /></div>;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t8) {
    t11 = <div className="relative">{t8}{t10}</div>;
    $[15] = t10;
    $[16] = t8;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== preferredName) {
    t12 = <span className="SacCard__name heading heading-sm">{preferredName}</span>;
    $[18] = preferredName;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ArkIcon iconName="birthday-cake" />;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = Number(age);
  let t15;
  if ($[21] !== birthDate || $[22] !== t || $[23] !== t14) {
    t15 = t("children:sac.age", {
      count: t14,
      birthday: birthDate
    });
    $[21] = birthDate;
    $[22] = t;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t15) {
    t16 = <span className=" SacCard__glyph-text flex gap-comfy-xs items-center">{t13}{t15}</span>;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== country) {
    t17 = country && <span className="SacCard__glyph-text flex gap-comfy-xs items-center"><ArkIcon iconName="globe-05" />{country}</span>;
    $[27] = country;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t16 || $[30] !== t17) {
    t18 = <div className="SacCard__list grid gap-snug-xl lg:gap-comfy-xs body body-md">{t16}{t17}</div>;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t12 || $[33] !== t18) {
    t19 = <div className="grid gap-comfy-sm pt-comfy-lg lg:pt-comfy-xl">{t12}{t18}</div>;
    $[32] = t12;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== handleClick || $[36] !== t11 || $[37] !== t19 || $[38] !== t6 || $[39] !== titleAndAlt) {
    t20 = <div className="flex flex-col relative"><Link href={t6} className="SacCard__bio-link" onClick={handleClick} title={titleAndAlt}>{t11}{t19}</Link></div>;
    $[35] = handleClick;
    $[36] = t11;
    $[37] = t19;
    $[38] = t6;
    $[39] = titleAndAlt;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  return t20;
};
export const SacCardSkeleton: React.FC = () => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute m-comfy-xs z-10"><Skeleton height={24} width={100} borderRadius="9999px" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="relative">{t0}<SkeletonImage width="100%" height="auto" style={{
        aspectRatio: "3/4"
      }} borderRadius="1em" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="SacCard__name"><Skeleton height={24} width="70%" /></span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="SacCard__glyph-text flex gap-comfy-xs items-center"><Skeleton height={20} width={20} borderRadius="0.25rem" /><Skeleton height={20} width={180} /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col">{t1}<div className="grid gap-comfy-sm pt-comfy-lg lg:pt-comfy-xl">{t2}<div className="SacCard__list grid gap-snug-xl lg:gap-comfy-xs">{t3}<div className="SacCard__glyph-text flex gap-comfy-xs items-center"><Skeleton height={20} width={20} borderRadius="0.25rem" /><Skeleton height={20} width={150} /></div></div></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};